/**
 * Get current organization ID from auth or impersonation context
 *
 * Priority:
 * 1. Impersonation (admin viewing another org) - active session only
 * 2. Working organization (persists after impersonation ends)
 * 3. Auth user's organization
 * 4. Fallback for development
 *
 * SECURITY: Only reads from authenticated sources, never user input
 */

/**
 * Get effective organization ID for storage scoping
 * @returns {string|null} Organization ID or null if unavailable
 */
export function getOrgId() {
  // 1. Check impersonation (highest priority - active session only)
  const impersonatingOrgId = localStorage.getItem('sr.impersonation.targetOrgId');
  if (impersonatingOrgId) {
    // Validate impersonation session exists and not expired
    try {
      const sessionData = localStorage.getItem('sr.impersonation.session');
      if (sessionData) {
        const session = JSON.parse(sessionData);
        // Check if session is still valid
        if (session.expiresAt && new Date(session.expiresAt) > new Date()) {
          return impersonatingOrgId;
        }
      }
    } catch (e) {
      console.error('[getOrgId] Failed to validate impersonation session:', e);
    }
  }

  // 2. Check working organization (persists after impersonation ends)
  const workingOrgId = localStorage.getItem('sr.working.targetOrgId');
  if (workingOrgId) {
    return workingOrgId;
  }

  // 3. Check current user's organization (from Supabase auth)
  try {
    const currentUser = JSON.parse(localStorage.getItem('current_user') || '{}');
    if (currentUser.organizationId) {
      return currentUser.organizationId;
    }

    // 4. Special handling for SUPER_ADMIN without org (for testing)
    if (currentUser.role === 'SUPER_ADMIN' || currentUser.role === 'super_admin') {
      // Use stable test org ID for Super Admin testing
      return '00000000-0000-0000-0000-000000000001';
    }
  } catch (e) {
    console.error('[getOrgId] Failed to parse current user:', e);
  }

  // 5. Final fallback for development
  console.warn('[getOrgId] No organization found, using fallback: default-org');
  return 'default-org';
}

/**
 * Check if organization-scoped storage is enabled
 * @returns {boolean}
 */
export function isOrgScopedStorageEnabled() {
  return localStorage.getItem('sr.feature.orgScopedStorage') === 'true';
}

/**
 * Set working organization (for Super Admins)
 * Used when impersonating or switching organizations
 * @param {string|null} orgId - Organization ID or null to clear
 */
export function setWorkingOrg(orgId) {
  if (orgId) {
    localStorage.setItem('sr.working.targetOrgId', orgId);
  } else {
    localStorage.removeItem('sr.working.targetOrgId');
  }
}

/**
 * Get working organization (if set)
 * @returns {string|null}
 */
export function getWorkingOrg() {
  return localStorage.getItem('sr.working.targetOrgId');
}

/**
 * Clear working organization
 * Useful for resetting Super Admin context
 */
export function clearWorkingOrg() {
  localStorage.removeItem('sr.working.targetOrgId');
}
